/* Copyright Flexday Solutions LLC, Inc - All Rights Reserved

 * Unauthorized copying of this file, via any medium is strictly prohibited

 * Proprietary and confidential

 * See file LICENSE.txt for full license details.

 */
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form } from 'formik';
import { object, string, bool, number } from 'yup';
import { LoadingButton } from '@mui/lab';

import {
  AlertStyled,
  DescriptionStyled,
  ExtAPISectionTitle,
  FormContainer,
  InputsContainer,
  LoaderStyled,
  PageHeader,
} from './styled';
import PageContainer from '../../../components/pageContainer';
import { SECTIONS } from '../../../constants/drawer';
import { LOAD_TITLE } from '../../../utils/functions';
import Loader from '../../../components/loader';
import AppForm, {
  AppSlider,
  AppTextField,
  AppSelectField,
  ToogleContainer,
  FormButtons,
} from '../../../components/form';
import {
  useLazyGetFileCollectionSettingsQuery,
  useUpdateFileCollectionSettingsMutation,
  useAddEmbeddingsMutation,
  useLazyGetIndividualFileCollectionQuery,
  useGetAllExternalAPIQuery,
} from '../../../redux/services/speciphicAsk';
import { EMBEDDING_CONFIG as API_ENDPOINT } from '../../../constants/filesCollectionSettings';
import { getChangedValues } from '../../../components/form/functions';
import UnauthorizedMessage from '../../../components/unauthorized';
import { ListItemText, MenuItem, Select } from '@mui/material';
import { EXTERNAL_EMBEDDINGS_API_ENDPOINT } from '../../../constants/externalAPIEndpoints';
import { useDispatch } from 'react-redux';
import { pushToast } from '../../../redux/reducers/toasts.slice';

const translationJSONPrefix = 'filesCollectionSettingsPage';

const unauthorizedTranslationPrefix = `${translationJSONPrefix}.unauthorizedMessages`;

const FILES_COLLECTION = `${translationJSONPrefix}.filesCollection`;
const PAGE_TITLE_SUFFIX = `${translationJSONPrefix}.pageTitleSuffix`;

const EXTERNAL_EMBEDDING_API_ENDPOINT = 'external-embedding-config';

const PAGE_TITLE = (t) =>
  `${t(`${translationJSONPrefix}.embeddingConfig.drawerMenuTitle`)} | ${t(
    FILES_COLLECTION,
  )} | ${t(PAGE_TITLE_SUFFIX)}`;

const defaultDataState = {
  name: '',
  isSemanticEnabled: false,
  finalTopK: '',
  retrieverRatio: '',
};

const marks = [
  {
    value: 0,
    label: '0',
  },
  {
    value: 0.3,
    label: '0.3',
  },
  {
    value: 0.7,
    label: '0.7',
  },
  {
    value: 1,
    label: '1',
  },
];

const nameOptions = [
  {
    label: 'Default',
    value: 'x',
  },
];

const formSchema = (t) => {
  const translationPrefix = (label) =>
    `${translationJSONPrefix}.embeddingConfig.form.${label}.errorMessages`;

  return object({
    name: string(),
    isSemanticEnabled: bool(),
    finalTopK: number().min(0, t(translationPrefix('finalTopK'))),
    retrieverRatio: number(),
  });
};

const formRenderFunction = (
  { values, errors, touched, dirty, isSubmitting, resetForm, setFieldValue },
  formSending,
  onUpdateEmbedding,
  updateEmbeddingLoading,
) => {
  const { t } = useTranslation();

  const translationPrefix = `${translationJSONPrefix}.embeddingConfig.form`;

  return (
    <Form>
      <InputsContainer>
        <ToogleContainer
          width="40%"
          name="isSemanticEnabled"
          label={t(`${translationPrefix}.isSemanticSearchEnabled.label`)}
          values={values}
          setFieldValue={setFieldValue}
          errors={errors}
          touched={touched}
        />
        <LoadingButton
          variant="contained"
          onClick={onUpdateEmbedding}
          loading={updateEmbeddingLoading}
          disabled={!values['isSemanticEnabled']}
        >
          {t(`${translationPrefix}.updateEmbeddings.label`)}
        </LoadingButton>
      </InputsContainer>

      <InputsContainer>
        <AppSelectField
          style={{ width: '100%' }}
          name="name"
          label={t(`${translationPrefix}.name.label`)}
          placeholder={t(`${translationPrefix}.name.placeholder`)}
          values={values}
          errors={errors}
          touched={touched}
          setFieldValue={setFieldValue}
          menuData={nameOptions}
          disabled={!values['isSemanticEnabled']}
        />
      </InputsContainer>

      <InputsContainer>
        <AppSlider
          style={{ width: '40%' }}
          name="retrieverRatio"
          label={t(`${translationPrefix}.retrieverRatio.label`)}
          values={values}
          setFieldValue={setFieldValue}
          errors={errors}
          touched={touched}
          step={0.1}
          min={0}
          max={1}
          marks={marks}
          valueLabelDisplay="auto"
          disabled={!values['isSemanticEnabled']}
        />
        <AppTextField
          style={{ width: '50%' }}
          name="finalTopK"
          label={t(`${translationPrefix}.finalTopK.label`)}
          placeholder={t(`${translationPrefix}.finalTopK.placeholder`)}
          values={values}
          setFieldValue={setFieldValue}
          errors={errors}
          touched={touched}
          type="number"
          disabled={!values['isSemanticEnabled']}
        />
      </InputsContainer>

      <FormButtons
        resetForm={resetForm}
        dirty={dirty}
        isSubmitting={isSubmitting || formSending}
      />
    </Form>
  );
};

const EmbeddingConfigPage = () => {
  const params = useParams();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  useEffect(() => LOAD_TITLE(document, PAGE_TITLE(t)), []);

  const [collectionId, setCollectionId] = useState();
  const [initialFormData, setInitialFormData] = useState(defaultDataState);
  const [formData, setFormData] = useState(defaultDataState);
  const [showAlert, setShowAlert] = useState({
    formUpdateError: false,
    formUpdateSuccess: false,
  });
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [externalEmbeddingId, setExternalEmbeddingId] = useState('');

  const [
    getFormData,
    {
      data: formDataDB,
      error: formError,
      isError: isGetFormError,
      isLoading: isGetFormLoading,
      isSuccess: isGetFormSuccess,
    },
  ] = useLazyGetFileCollectionSettingsQuery();

  const [
    getExternalEmbedding,
    { data: externalEmbedding, isLoading: isExternalEmbeddingLoading },
  ] = useLazyGetFileCollectionSettingsQuery();

  useEffect(() => {
    if (externalEmbedding)
      setExternalEmbeddingId(externalEmbedding?.externalEmbeddingId);
  }, [externalEmbedding]);

  const {
    data: extAllAPIs,
    error: extError,
    isFetching: extAllAPIsFetching,
  } = useGetAllExternalAPIQuery({
    endpoint: EXTERNAL_EMBEDDINGS_API_ENDPOINT,
  });

  const [
    getFilesCollectionInfo,
    {
      data: filesCollectionInfo,
      error: filesCollectionError,
      isError: isGetFCError,
      isLoading: isGetFCLoading,
      isSuccess: isGetFCSuccess,
    },
  ] = useLazyGetIndividualFileCollectionQuery();

  useEffect(() => {
    if (params?.collectionId) {
      setCollectionId(params?.collectionId);
      getFilesCollectionInfo({ collectionId: params?.collectionId });
      getFormData({
        collectionId: params.collectionId,
        endpoint: API_ENDPOINT,
      });
      getExternalEmbedding({
        collectionId: params.collectionId,
        endpoint: EXTERNAL_EMBEDDING_API_ENDPOINT,
      });
    }
  }, [params?.collectionId]);

  const [
    updateSettings,
    {
      data: formDataUpdateDB,
      isError: isUpdateFormError,
      isLoading: isUpdateFormLoading,
      isSuccess: isUpdateFormSuccess,
    },
  ] = useUpdateFileCollectionSettingsMutation();

  const [
    updateExternalEmbedding,
    {
      data: externalEmbeddingUpdated,
      error: externalEmbeddingError,
      isLoading: isExternalEmbeddingUpdating,
      isSuccess: isExternalEmbeddingSuccess,
      isError: isExternalEmbeddingError,
    },
  ] = useUpdateFileCollectionSettingsMutation();

  useEffect(() => {
    if (isExternalEmbeddingSuccess)
      dispatch(
        pushToast({
          message: t(
            `${translationJSONPrefix}.embeddingConfig.externalEmbedding.toastSuccess`,
          ),
          severity: 'success',
        }),
      );
  }, [isExternalEmbeddingSuccess]);

  useEffect(() => {
    if (isExternalEmbeddingError)
      dispatch(
        pushToast({
          message: externalEmbeddingError?.data?.message,
          severity: 'error',
        }),
      );
  }, [isExternalEmbeddingError]);

  const [
    addEmbeddings,
    {
      data: addEmbResponse,
      isError: isAddEmbError,
      isLoading: isAddEmbLoading,
      isSuccess: isAddEmbSuccess,
    },
  ] = useAddEmbeddingsMutation();

  const closeAlert = () => {
    const tempAlert = { ...showAlert };
    Object.keys(tempAlert).forEach((a) => (tempAlert[a] = false));
    setShowAlert(tempAlert);
  };

  useEffect(() => {
    const tempAlert = { ...showAlert };
    tempAlert.formUpdateError = isUpdateFormError;
    setShowAlert(tempAlert);
  }, [isUpdateFormError]);

  useEffect(() => {
    const tempAlert = { ...showAlert };
    tempAlert.formUpdateSuccess = isUpdateFormSuccess;
    setShowAlert(tempAlert);
  }, [isUpdateFormSuccess]);

  useEffect(() => {
    if (showAlert.formUpdateError || showAlert.formUpdateSuccess)
      setTimeout(closeAlert, 5000);
  }, [showAlert.formUpdateError, showAlert.formUpdateSuccess]);

  useEffect(() => {
    if (isGetFormSuccess) {
      const {
        name = '',
        isSemanticEnabled = false,
        finalTopK = '',
        retrieverRatio = '',
      } = formDataDB;

      const data = {
        name: name === '' ? 'x' : name,
        isSemanticEnabled,
        finalTopK,
        retrieverRatio,
      };

      setFormData(data);
      setInitialFormData(data);
    }
  }, [formDataDB]);

  const handleUpdateEmbeddings = () => {
    addEmbeddings({ collectionId });
  };

  if (formError?.status === 403 || filesCollectionError?.status === 403)
    return (
      <UnauthorizedMessage
        drawerOpen={isDrawerOpen}
        drawer={SECTIONS.FILES_COLLECTION_SETTINGS}
        description={t(`${unauthorizedTranslationPrefix}.list`)}
      />
    );

  return (
    <PageContainer
      isDrawerOpen={(res) => setIsDrawerOpen(res)}
      drawer={SECTIONS.FILES_COLLECTION_SETTINGS}
      collectionId={collectionId}
    >
      <PageHeader>
        {`${filesCollectionInfo?.name || '...'} / ${t(
          `${translationJSONPrefix}.embeddingConfig.pageHeader`,
        )}`}
      </PageHeader>

      <DescriptionStyled>{collectionId}</DescriptionStyled>

      {collectionId && !isGetFormLoading ? (
        <FormContainer>
          <AlertStyled
            onClose={closeAlert}
            severity={showAlert.formUpdateError ? 'error' : 'success'}
            style={{
              marginTop:
                showAlert.formUpdateError || showAlert.formUpdateSuccess
                  ? 10
                  : -60,
            }}
          >
            {showAlert.formUpdateError &&
              t(
                `${translationJSONPrefix}.embeddingConfig.alert.updateFormError`,
              )}
            {showAlert.formUpdateSuccess &&
              t(
                `${translationJSONPrefix}.embeddingConfig.alert.updateFormSuccess`,
              )}
          </AlertStyled>

          <AppForm
            initialValues={formData}
            validationSchema={formSchema(t)}
            onSubmit={(values, { setSubmitting }) => {
              const valuesToSend = getChangedValues(values, initialFormData);

              updateSettings({
                collectionId,
                data: valuesToSend,
                endpoint: API_ENDPOINT,
              });

              setSubmitting(false);
            }}
            formRenderFunction={(formikState) =>
              formRenderFunction(
                formikState,
                isUpdateFormLoading,
                handleUpdateEmbeddings,
                isAddEmbLoading,
              )
            }
          />
        </FormContainer>
      ) : (
        <Loader label={t(`${translationJSONPrefix}.loadingMessage`)} />
      )}

      <ExtAPISectionTitle>
        {t(`${translationJSONPrefix}.embeddingConfig.externalEmbedding.title`)}
      </ExtAPISectionTitle>

      {(isExternalEmbeddingUpdating || isExternalEmbeddingLoading) && (
        <LoaderStyled style={{ margin: '20px 0' }} />
      )}

      <Select
        fullWidth
        value={externalEmbeddingId}
        onChange={(e) => {
          setExternalEmbeddingId(e.target.value);

          updateExternalEmbedding({
            collectionId,
            data: {
              id: e.target.value,
            },
            endpoint: EXTERNAL_EMBEDDING_API_ENDPOINT,
          });
        }}
      >
        {extAllAPIsFetching && (
          <MenuItem value={''} disabled>
            <ListItemText primary="Loading..." />
          </MenuItem>
        )}

        {extAllAPIs?.map((extEmb, i) => (
          <MenuItem key={i} value={extEmb.id}>
            <ListItemText
              primary={extEmb?.name}
              secondary={extEmb?.apiEndpoint}
            />
          </MenuItem>
        ))}
      </Select>
    </PageContainer>
  );
};

export default EmbeddingConfigPage;
